import React from 'react';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WeekNavigator from '../common/week_navigator';
import PlannerWeekActionsPopupMenu from './planner_week_actions_popup_menu';
import {
  currentViewWeekStartDateStr,
  dateMinusDays,
  daysArrayIndex,
  dbWeekStartDatesAndDayIndexesCoveringCalendarRange,
  mealIDsCandidatesForAddingToMealBasket,
} from '../../services/planner';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  clearPlanner,
  getOrCreatePlannerWeeks,
  plannerCopyPreviousWeekMeals,
} from '../../operations/planner_operations';
import {
  useCreateContentEntryMutation,
  useCreateMealMutation,
  useCreateNoteMutation,
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useLazyGetContentEntriesByParentQuery,
  useLazyGetDbPlannerWeekQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullMealsByParentQuery,
  useLazyGetNotesByParentQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { deduplicate } from '../../services/arrays';
import { mergeMyDayActionRecords } from '../../services/programmes';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { scaleMealIngredientsOperation } from '../../operations/meal_operations';
import { mealWithScaledIngredientsReplaced } from '../../services/meals';

const PlannerNavigationBar = ({ plannerViewWeekStartDate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [lazyGetDbPlannerWeek] = useLazyGetDbPlannerWeekQuery();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const [lazyGetNotesByParent] = useLazyGetNotesByParentQuery();

  const [lazyGetContentEntriesByParent] =
    useLazyGetContentEntriesByParentQuery();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [createNote] = useCreateNoteMutation();

  const [createContentEntry] = useCreateContentEntryMutation();

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ) */

  const thisViewWeekStartDate = currentViewWeekStartDateStr(
    plannerViewWeekStartDay,
  );
  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  return (
    <Box
      display="flex"
      className="plannerNavigationBar"
      justifyContent="center"
      style={{ color: 'var(--app-primary-font-color)' }}>
      <div style={{ margin: 'auto 0', padding: '0 10px' }}>
        <Button
          onClick={() => navigate(`/planner/${thisViewWeekStartDate}`)}
          variant="text"
          size="small"
          color="primary"
          style={{
            fontSize: '14px',
            color: 'var(--app-primary-font-color)',
            border: '1px solid white',
            padding: '0 5px',
          }}>
          This week
        </Button>
      </div>
      <div style={{ flexGrow: 1, margin: 'auto 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            margin: 'auto 0',
          }}>
          <WeekNavigator
            startDate={plannerViewWeekStartDate}
            onChangeStartDate={(startDate) => navigate(`/planner/${startDate}`)}
            arrowStyle={{ color: 'var(--app-primary-font-color)' }}
            innerWeekWidth={160}
          />
        </div>
      </div>
      <div style={{ padding: '0 10px' }}>
        <PlannerWeekActionsPopupMenu
          fabStyle={{ color: 'var(--app-primary-font-color)' }}
          onDeleteAllMeals={() => {
            const deleteUntilDate = dayjs(plannerViewWeekStartDate)
              .add(7, 'day')
              .toISOString();
            clearPlanner(
              plannerViewWeekStartDate,
              deleteUntilDate,
              lazyGetOrCreateDbPlannerWeekByStartDate,
              dispatch,
              deleteMeal,
              deleteNote,
              deleteContentEntry,
              updateDbPlannerWeek,
            );
          }}
          onAddWeekMealsToMealsBasket={async (scaleToServings) => {
            const startOfNextWeek = dayjs(plannerViewWeekStartDate)
              .add(7, 'day')
              .toISOString();
            const dbWeekRecords =
              dbWeekStartDatesAndDayIndexesCoveringCalendarRange(
                plannerViewWeekStartDate,
                startOfNextWeek,
              );
            const dbWeekStartDates = deduplicate(
              dbWeekRecords.map((dbWeek) => dbWeek.dbWeekStartDate),
            );
            const plannerWeeks = await getOrCreatePlannerWeeks(
              dbWeekStartDates,
              lazyGetOrCreateDbPlannerWeekByStartDate,
            );
            const mealIDsToAddToBasket = [];
            dbWeekRecords.forEach((dbWeekRecord) => {
              const plannerWeek = plannerWeeks[dbWeekRecord.dbWeekStartDate];
              if (plannerWeek) {
                const dayIndexStr = dbWeekRecord.dayIndex.toString();
                const plannerDay =
                  plannerWeek.days[daysArrayIndex(dayIndexStr)];
                const mealIDs = mealIDsCandidatesForAddingToMealBasket(
                  plannerDay,
                  myDayActionRecords,
                );
                mealIDs.filter(Boolean).forEach((mealID) => {
                  mealIDsToAddToBasket.push(mealID);
                });
              }
            });
            const fullMealsToAddToBasket = await Promise.all(
              mealIDsToAddToBasket.map(async (mealID) => {
                const { data: fullMeal } = await lazyGetMeal(mealID);
                return fullMeal;
              }),
            );
            const scaledMealsToAddToBasket = await Promise.all(
              fullMealsToAddToBasket.map(async (fullMeal) => {
                if (!scaleToServings) {
                  return fullMeal;
                }
                const scaleFactor =
                  scaleToServings / (fullMeal.recipes[0]?.servings || 1);
                const scaledIngredients = await scaleMealIngredientsOperation(
                  fullMeal,
                  scaleFactor,
                  customerFoodDataID,
                );
                return mealWithScaledIngredientsReplaced(
                  fullMeal,
                  scaleToServings,
                  scaledIngredients,
                  scaleFactor,
                );
              }),
            );
            dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
          }}
          onCopyPreviousWeekMeals={async () => {
            return plannerCopyPreviousWeekMeals(
              plannerViewWeekStartDate,
              dateMinusDays(plannerViewWeekStartDate, 7),
              lazyGetDbPlannerWeek,
              lazyGetFullMealsByParent,
              lazyGetNotesByParent,
              lazyGetContentEntriesByParent,
              createMeal,
              createNote,
              createContentEntry,
              updateMeal,
              updateDbPlannerWeek,
              currentSpaceMembership,
              customerFoodDataID,
            );
          }}
        />
      </div>
    </Box>
  );
};

PlannerNavigationBar.propTypes = {
  plannerViewWeekStartDate: PropTypes.string.isRequired,
};

export default PlannerNavigationBar;
